/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { get, head, orderBy, toString } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import * as Sentry from '@sentry/browser';
import {
  LenderTable,
  DropdownEllipsisBtn,
  Text,
  ActionButton,
  uploadSingleFile,
  Upload,
  FilePickerInput,
} from '@blend/enterprise-components';
import { DocumentsTableColumnTypes } from './types';
import { addDocsToLending, deleteDocument, downloadDocument } from 'blb-api/loans';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';
import { DocumentIndicatorColumn } from './components/DocumentIndicatorColumn';
import * as utils from './utils';
import { BlendBridge } from 'blb-types/blendBridge';
import { DashboardSection } from 'blb-components/AuthenticatedApp/DashboardPage/DashboardSection';
import { uploadFile } from 'blb-api/files';
import { FormFields } from './components/UploadFileModal';
import { UploadFileModal } from 'blb-components/AuthenticatedApp/DashboardPage/Docs/components/UploadFileModal';

enum SortDir {
  ASC = 'asc',
  DESC = 'desc',
}

export const TABLE_HEADER_HEIGHT = 40;
export const HEADER_WRAPPER_HEIGHT = 174;
export const TABLE_CONTAINER_VERTICAL_PADDING = 32;

const styles = StyleSheet.create({
  headerBtnWrapper: {
    display: 'flex',
  },
  rowStyle: {
    ':nth-child(1n) td:first-child': {
      padding: 0,
      width: 16,
    },
  },
  tableEmptyStateTitleWrapper: {
    paddingBottom: 4,
  },
  tableEmptyStateWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    height: `calc(100vh - ${TABLE_HEADER_HEIGHT + HEADER_WRAPPER_HEIGHT + TABLE_CONTAINER_VERTICAL_PADDING}px)`,
    justifyContent: 'center',
  },
});

const { useState, useMemo, useRef } = React;

export interface DocumentsTableProps {
  documents: BlendBridge.API.Document[];
  fetchDocs: () => Promise<void>;
  loan: BlendBridge.API.Loan;
  sessionUserId: string;
}

export type DocumentUploadStatus = 'DEFAULT' | 'UPLOADING' | 'ERROR';

const DEFAULT_SORT_COLUMN = DocumentsTableColumnTypes.DocumentUploadedOnColumn;

const renderEmptyState = () => (
  <div className={css(styles.tableEmptyStateWrapper)} data-it="DocumentsTableEmptyState">
    <div className={css(styles.tableEmptyStateTitleWrapper)}>
      <Text fontSize="lg" weight="bold">
        No documents uploaded
      </Text>
    </div>
    <Text displayBlock>Click upload to attach a document to this loan</Text>
  </div>
);

export const DocsTab: React.FC<DocumentsTableProps> = ({ loan, documents, sessionUserId, fetchDocs }) => {
  const Toaster = useToasts();
  const inputElRef = useRef<HTMLInputElement>(null);
  const [sortColumn, setSortColumn] = useState<DocumentsTableColumnTypes>(DEFAULT_SORT_COLUMN);
  const [sortDir, setSortDir] = useState(SortDir.DESC);
  const [uploadStatus, setUploadStatus] = useState<DocumentUploadStatus>('DEFAULT');
  const [isUploadFileModalOpen, setUploadFileModalOpen] = useState<boolean>(false);
  const [fileToBeUploaded, setFileToBeUploaded] = useState<File | null>(null);

  const columnConfigs = utils.DOCUMENTS_COLUMN_CONFIGS;
  const orderedColumnConfigs = utils.DOCUMENT_TABLE_COLUMNS_DISPLAY_ORDER.map(
    componentType => columnConfigs[componentType],
  );
  const handleHeaderClick = (columnToSortBy: DocumentsTableColumnTypes) => {
    if (sortColumn === columnToSortBy) {
      if (sortDir === SortDir.DESC) {
        setSortDir(SortDir.ASC);
      } else {
        // sortDir is ASC, reset back to default sort
        setSortColumn(DEFAULT_SORT_COLUMN);
        setSortDir(SortDir.DESC);
      }
    } else {
      // new column to sort by
      setSortColumn(columnToSortBy);
      setSortDir(SortDir.DESC);
    }
  };

  const memoizedSortedDocuments = useMemo(
    () =>
      orderBy(
        documents,
        doc => {
          const accessedValue = columnConfigs[sortColumn].accessorFn(loan, doc);
          return accessedValue;
        },
        sortDir,
      ),
    [documents, sortColumn, sortDir],
  );
  const headers = utils.generateTableRowHeader(orderedColumnConfigs, handleHeaderClick, sortColumn, sortDir);
  const sections = memoizedSortedDocuments.map((document, idx) => [
    <DocumentIndicatorColumn document={document} key={`${document._id}-indicator`} />,
    ...utils.generateTableRowDataCols(loan, document, orderedColumnConfigs, idx),
    <DropdownEllipsisBtn
      actions={utils.getRowActionConfigs(
        {
          delete: async () => {
            try {
              await deleteDocument(loan.loanId, document._id);
            } catch (e) {
              Toaster?.addToast({
                message: 'Unable to delete document, please contact support-closing@blend.com',
                theme: 'error',
              });
            }
            await fetchDocs();
          },
          download: async () => {
            await downloadDocument({
              documentId: document._id,
              documentTypeLabel: document.type,
              loanId: loan.loanId,
              Toaster,
            });
          },
        },
        document,
        sessionUserId,
      )}
      anchorPosition={'bottom-end'}
      id={`DocumentsTableRow-${document._id}`}
      key={`${document._id}-rowAction`}
    />,
  ]);
  const columnWidths = utils.getTableColumnWidths(orderedColumnConfigs);
  const docsTableConfig = [
    {
      content: (
        <LenderTable
          columnWidths={columnWidths}
          customEmptyComponent={renderEmptyState()}
          data-it="DocumentsTable"
          headerComponents={headers}
          rowStyle={styles.rowStyle}
          sections={sections}
        />
      ),
      hideTitle: true,
      title: 'docsTable',
    },
  ];

  const handleFileUploadButtonClicked = () => {
    // click on input element to select a file
    inputElRef.current!.click();
  };

  // Once a file is selected from client file system
  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = head(e.target.files);
    if (file) {
      setUploadFileModalOpen(true);
      setFileToBeUploaded(file);
    }
  };

  const handleCloseUploadModal = () => {
    setUploadFileModalOpen(false);
    setUploadStatus('DEFAULT');
  };

  const handleConfirmUploadFile = async (fields: FormFields) => {
    if (!fileToBeUploaded) {
      Toaster?.addToast({
        message: 'No file found to be uploaded, please try again',
        theme: 'error',
      });
      return;
    }
    setUploadStatus('UPLOADING');
    await uploadSingleFile({
      file: fileToBeUploaded,
      handleUploadEvent: async (_fileId: string, file: File, upload: Upload) => {
        if (upload.fileName && upload.url) {
          try {
            // update Lending
            await addDocsToLending({
              files: [{ fileKey: upload.url, fileName: upload.fileName, type: fields.docType }],
              loanId: loan.loanId,
            });
            await fetchDocs();
            handleCloseUploadModal();
          } catch (err) {
            Sentry.captureException(`failed to upload file to lending with err: ${get(err, 'message', toString(err))}`);
            setUploadStatus('ERROR');
          }
          // upload to file service fails
        } else if (upload.errorMessage) {
          Sentry.captureException(`failed to upload file: ${file.name}, type: ${file.type}, size: ${file.size}`);
          setUploadStatus('ERROR');
        }
      },
      otherUploadParams: { loanId: loan.loanId },
      uploadFile,
    });
  };

  const getDashboardHeaderAction = () => {
    return (
      <div className={css(styles.headerBtnWrapper)}>
        <FilePickerInput
          accept={'.png,.jpg,.jpeg,.tif,.tiff,.svg,.ico,.pdf'}
          id="DocsTab-documentsUploadInput"
          onChange={handleFileSelected}
          ref={inputElRef}
        >
          <ActionButton
            btnSize="md"
            disabled={false}
            icon="file_upload"
            id="DocsTab-documentsUploadButton"
            label="Upload"
            onClick={handleFileUploadButtonClicked}
            tooltipPosition="bottom-end"
          />
        </FilePickerInput>
      </div>
    );
  };

  return (
    <>
      <DashboardSection
        headerActions={getDashboardHeaderAction()}
        sectionConfigs={docsTableConfig}
        sectionHeader="Documents"
        wideCard
      />
      <UploadFileModal
        isOpen={isUploadFileModalOpen}
        onClose={handleCloseUploadModal}
        onSubmit={handleConfirmUploadFile}
        setUploadStatus={setUploadStatus}
        uploadFileName={fileToBeUploaded?.name}
        uploadStatus={uploadStatus}
      />
    </>
  );
};
